import { useLazyQuery, useMutation } from "@apollo/client";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { CustomGridColDef, DataGrid } from "components/DataGrid";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { SubmissionStatus, SubmissionType } from "generated-graphql/graphql";
import {
  BULK_SUBMISSIONS_SAFE_TO_SEND_TO_REVIEWERS_QUERY,
  SEND_SUBMISSIONS_TO_REVIEWERS_MUTATION,
} from "queries/submissions";
import { useEffect, useMemo } from "react";
import { formatDateLikeForDisplay } from "util/dates";

import { Row } from "./types";
import { partitionSubmissions } from "./utils";

interface SendToReviewersDialogProps {
  open: boolean;
  onClose: () => void;
  submissions: Row[];
  onSuccess?: () => void;
}

export function SendToReviewersDialog({
  open,
  onClose,
  submissions,
  onSuccess,
}: SendToReviewersDialogProps) {
  const alerts = useAlerts();

  // Partition virtual events from real submissions
  const { uniqueSubmissionIds, virtualEvents } = useMemo(
    () => partitionSubmissions(submissions),
    [submissions]
  );

  const [checkSafety, { data: safetyData, loading: checkingLoading }] = useLazyQuery(
    BULK_SUBMISSIONS_SAFE_TO_SEND_TO_REVIEWERS_QUERY
  );

  const [sendToReviewers, { loading: mutationLoading }] = useMutation(
    SEND_SUBMISSIONS_TO_REVIEWERS_MUTATION,
    {
      onCompleted: (data) => {
        if (data.sendSubmissionsToReviewers.succeeded) {
          const returnedJobId = data.sendSubmissionsToReviewers.jobId ?? null;
          alerts.success(
            `Successfully queued ${
              uniqueSubmissionIds.length
            } submission(s) to be sent to reviewers${
              returnedJobId ? ` (Job ID: ${returnedJobId})` : ""
            }`
          );
          handleClose();
          onSuccess?.();
        }
      },
      onError: (error) => {
        alerts.error(`Failed to send submissions to reviewers: ${error.message}`);
      },
      refetchQueries: ["SubmissionVirtualEvents", "GetSubmission"],
    }
  );

  const loading = checkingLoading || mutationLoading;

  useEffect(() => {
    if (open && uniqueSubmissionIds.length > 0) {
      checkSafety({
        variables: {
          submissionIds: uniqueSubmissionIds,
        },
      });
    }
  }, [open, uniqueSubmissionIds, checkSafety]);

  const handleClose = () => {
    onClose();
  };

  const { safeSubmissions, unsafeSubmissions } = useMemo(() => {
    if (!safetyData?.bulkSubmissionsSafeToSendToReviewers) {
      // When no safety data is available yet, only show virtual events as unsafe
      const virtualEventUnsafeItems = virtualEvents.map((ve) => ({
        id: ve.id,
        isSafe: false,
        reasons: ["Virtual events must be created as submissions before sending to reviewers"],
        submission: {
          id: ve.id,
          title: ve.title ?? "",
          submissionType: ve.submission?.submissionType as SubmissionType,
          status: SubmissionStatus.InProgress,
          dueDate: ve.deadline,
          facilitySubmissions:
            ve.facilities?.map((f, index) => ({
              id: `${ve.id}-facility-${index}`,
              facility: f,
            })) || [],
        },
      }));
      return {
        safeSubmissions: [],
        unsafeSubmissions: virtualEventUnsafeItems,
      };
    }

    const safe = safetyData.bulkSubmissionsSafeToSendToReviewers.filter(
      (item: { isSafe: boolean }) => item.isSafe
    );
    const unsafe = safetyData.bulkSubmissionsSafeToSendToReviewers.filter(
      (item: { isSafe: boolean }) => !item.isSafe
    );

    // Add virtual events as unsafe submissions
    const virtualEventUnsafeItems = virtualEvents.map((ve) => ({
      id: ve.id,
      isSafe: false,
      reasons: ["Virtual events must be created as submissions before sending to reviewers"],
      submission: {
        id: ve.id,
        title: ve.title ?? "",
        submissionType: ve.submission?.submissionType as SubmissionType,
        status: SubmissionStatus.InProgress,
        dueDate: ve.deadline,
        facilitySubmissions:
          ve.facilities?.map((f, index) => ({
            id: `${ve.id}-facility-${index}`,
            facility: f,
          })) || [],
      },
    }));

    return {
      safeSubmissions: safe,
      unsafeSubmissions: [...unsafe, ...virtualEventUnsafeItems],
    };
  }, [safetyData, virtualEvents]);

  const handleSubmit = async () => {
    await sendToReviewers({
      variables: {
        submissionIds: uniqueSubmissionIds,
      },
    });
  };

  const columns: CustomGridColDef<(typeof safeSubmissions)[number]>[] = [
    {
      field: "dueDate",
      headerName: "Due Date",
      width: 100,
      valueGetter: (value, row) =>
        row.submission.dueDate ? formatDateLikeForDisplay(row.submission.dueDate) : "",
    },
    {
      field: "facility",
      headerName: "Facility",
      flex: 1,
      minWidth: 120,
      valueGetter: (value, row) =>
        row.submission.facilitySubmissions
          ?.map((fs: { facility: { name: string } }) => fs.facility.name)
          .join(", ") || "",
    },
    {
      field: "submissionType",
      headerName: "Submission Type",
      width: 130,
      valueGetter: (value, row) => prettyPrintEnumValue(row.submission.submissionType),
    },
    {
      field: "title",
      headerName: "Title",
      flex: 1,
      minWidth: 150,
      valueGetter: (value, row) => row.submission.title,
    },
  ];

  const blockedColumns: CustomGridColDef<(typeof unsafeSubmissions)[number]>[] = [
    ...columns,
    {
      field: "reason",
      headerName: "Reason Blocked",
      flex: 1,
      minWidth: 250,
      valueGetter: (value, row) => row.reasons.join("; "),
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      slotProps={{
        paper: {
          sx: {
            width: 800,
            maxWidth: "90vw",
          },
        },
      }}
    >
      <DialogTitle>Send to Reviewers</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            This action will update the selected ({uniqueSubmissionIds.length}) submission(s) status
            to <strong>🔍 In Review</strong> and send these submissions to reviewers.
            {virtualEvents.length > 0 && (
              <>
                <br />
                <strong>Note:</strong> {virtualEvents.length} virtual event(s) will be skipped. They
                must be created first using "Change Status".
              </>
            )}
          </Typography>

          <Alert severity="info">
            <Typography variant="body2">
              <strong>Note:</strong> This operation runs in the background.
            </Typography>
            <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
              • Submissions must have reviewers assigned
              <br />• Staff reviewers must complete their review first
              <br />• Emails will be sent only to incomplete reviewers
            </Typography>
          </Alert>

          {safetyData && (
            <Box>
              {safeSubmissions.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Submissions to Send ({safeSubmissions.length})
                  </Typography>
                  <DataGrid
                    rows={safeSubmissions}
                    columns={columns}
                    disableRowSelectionOnClick
                    pagination
                    paginationMode="client"
                    pageSizeOptions={[5, 10]}
                    hideFooterPagination={false}
                    initialState={{
                      pagination: {
                        paginationModel: { pageSize: 10 },
                      },
                    }}
                    sx={{ maxHeight: 300 }}
                  />
                </Box>
              )}

              {unsafeSubmissions.length > 0 && (
                <Box>
                  <Alert
                    severity={safeSubmissions.length === 0 ? "error" : "warning"}
                    sx={{ mb: 1 }}
                  >
                    {safeSubmissions.length === 0
                      ? "None of the selected submissions can be sent to reviewers. See details below."
                      : `${unsafeSubmissions.length} submission(s) cannot be sent to reviewers and will be skipped`}
                  </Alert>
                  <DataGrid
                    rows={unsafeSubmissions}
                    columns={blockedColumns}
                    disableRowSelectionOnClick
                    pagination
                    paginationMode="client"
                    pageSizeOptions={[5, 10]}
                    hideFooterPagination={false}
                    initialState={{
                      pagination: {
                        paginationModel: { pageSize: 10 },
                      },
                    }}
                    sx={{ maxHeight: 600 }}
                  />
                </Box>
              )}

              {unsafeSubmissions.length === 0 && safeSubmissions.length === 0 && (
                <Box sx={{ mb: 2 }}>
                  <Alert severity={"warning"} sx={{ mb: 1 }}>
                    None of the selected submissions can be sent to reviewers.
                  </Alert>
                </Box>
              )}
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading || safeSubmissions.length === 0}
        >
          {mutationLoading ? "Queueing..." : "Send to Reviewers"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
